import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  AuthenticationService,
  ContractStatus,
  isTwoFactorRequired,
  isTwoFactorSetupRequired,
  LoginUserToken,
} from '../../authentication.service';
import { Router, RouterLink } from '@angular/router';
import { FormValidationErrorHandler } from '@styleguide/form/form-validation-error-handler.service';
import { FormValidationErrors } from '@styleguide/form/form-validation-errors';
import { Observable } from 'rxjs';
import { HTTP_STATUS_OFFLINE } from '../../../http/httpError';
import { UserCredentialsStore } from '../../user-credentials-store.service';
import { ConfigService } from '../../../config/config.service';
import { ICustomerAppsConfig } from '../../../config/config.module';
import { PrivacyService } from '../../../privacy/privacy.service';
import { AsyncPipe } from '@angular/common';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { ErrorsComponent } from '@styleguide/error/errors/errors.component';
import { FormFieldComponent } from '@styleguide/form/form-field/form-field.component';
import { PasswordInputControlComponent } from '@styleguide/form/password-input-control/password-input-control.component';
import { SelectLoginUserComponent } from '../select-login-user/select-login-user.component';
import { TwoFactorFormComponent } from '../two-factor-form/two-factor-form.component';
import { TwoFactorSetupComponent } from '../two-factor-setup/two-factor-setup.component';
import jwtDecode from 'jwt-decode';

@Component({
  selector: 'kgr-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingDirective,
    ReactiveFormsModule,
    RouterLink,
    ErrorsComponent,
    FormFieldComponent,
    PasswordInputControlComponent,
    SelectLoginUserComponent,
    TwoFactorFormComponent,
    TwoFactorSetupComponent,
    AsyncPipe,
  ],
})
export class LoginFormComponent implements OnInit {
  @HostBinding('class.kgr-animated')
  @HostBinding('class.kgr-block')
  cssDefaultClasses = true;

  @HostBinding('class.kgr-animated--rippleSuccess')
  get rippleSuccess(): boolean {
    return this.loginUserTokens.length > 0 && !this.unexpectedError && !this.errorBadCredentials;
  }

  @HostBinding('class.kgr-animated--rippleError')
  get rippleError(): boolean {
    return this.unexpectedError || this.errorBadCredentials;
  }

  @Output() loginResult = new EventEmitter<boolean>();
  @Output() loginSuccess = new EventEmitter<void>();

  @Input() organizationId?: number; // restrict login to this organization only
  @Input() redirectUrl?: string;
  @Input() redirectCommands?: string[];
  @Input() redirectReplaceUrl = false;
  @Input() showEvenIfAlreadyLoggedIn = false;

  @Input() presetUsername = '';

  public isLoading = false;

  form: UntypedFormGroup;
  userCredentials$ = this.userCredentialsStore.userCredentials$;

  unexpectedError = false;
  unexpectedServerError = false;
  errorBadCredentials = false;
  errorOffline = false;

  validationErrors$!: Observable<FormValidationErrors>;
  treatAllControlsAsDirty = false;

  loginUserTokens: LoginUserToken[] = [];
  twoFactorToken: string | null = null;
  twoFactorSetupContext: { loginContextId: number; authToken: string } | null = null;

  privacyUrl?: string;
  isCustomizedApp = false;

  constructor(
    private cd: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private userCredentialsStore: UserCredentialsStore,
    private router: Router,
    private formValidationErrorHandler: FormValidationErrorHandler,
    private privacyService: PrivacyService,
    private configService: ConfigService<ICustomerAppsConfig>,
  ) {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      username: new FormControl<string>(this.presetUsername, [Validators.required]),
      password: new FormControl<string>('', [Validators.required]),
    });

    this.privacyUrl = this.configService.config.privacyUrl;
    this.isCustomizedApp = !!this.configService.config.organization;

    this.form.valueChanges.subscribe(() => {
      this.resetErrors();
    });
    this.validationErrors$ = this.formValidationErrorHandler.getErrorsStream(this.form, {});
  }

  onLogin() {
    this.treatAllControlsAsDirty = true;
    this.loginUserTokens = [];
    if (this.form.valid) {
      this.resetErrors();
      this.isLoading = true;
      this.authenticationService.fetchLoginUserTokens(this.form.value.username, this.form.value.password, this.organizationId).subscribe(
        (response) => {
          if (isTwoFactorRequired(response)) {
            this.twoFactorToken = response.two_factor_token;
            this.isLoading = false;
            this.cd.markForCheck();
            return;
          }

          if (isTwoFactorSetupRequired(response)) {
            const authToken = response.tokens[0]?.token;
            const decoded = authToken ? jwtDecode<{ username: string }>(authToken) : null;
            const loginContextId = decoded ? parseInt(decoded.username, 10) : null;
            if (authToken && loginContextId) {
              this.twoFactorSetupContext = { loginContextId, authToken };
            } else {
              this.unexpectedError = true;
            }
            this.isLoading = false;
            this.cd.markForCheck();
            return;
          }

          const loginUserTokens = response as LoginUserToken[];
          if (loginUserTokens.length === 0) {
            this.errorBadCredentials = true;
          } else if (
            loginUserTokens.length === 1 &&
            (loginUserTokens[0].contractStatus === null ||
              loginUserTokens[0].contractStatus === undefined ||
              loginUserTokens[0].contractStatus === ContractStatus.running)
          ) {
            if (loginUserTokens[0].token && this.authenticationService.loginByToken(loginUserTokens[0].token)) {
              this.onLoginSuccess();
            } else {
              this.unexpectedError = true;
            }
          } else {
            this.loginUserTokens = loginUserTokens;
          }
          this.isLoading = false;
          this.cd.markForCheck();
        },
        (apiError) => {
          console.log('error', apiError);
          switch (apiError.code) {
            case HTTP_STATUS_OFFLINE:
            case 0:
              this.errorOffline = true;
              break;
            case 404:
            case 401:
            case 403:
              this.errorBadCredentials = true;
              break;
            default:
              this.unexpectedServerError = true;
              break;
          }
          this.isLoading = false;
          this.cd.markForCheck();
        },
      );
    }
  }

  onLoginSuccess() {
    this.isLoading = false;
    this.loginResult.emit(true);
    this.loginSuccess.emit();
    if (this.redirectUrl) {
      this.router.navigateByUrl(this.redirectUrl, { replaceUrl: this.redirectReplaceUrl });
    } else if (this.redirectCommands?.length > 0) {
      this.router.navigate(this.redirectCommands, { replaceUrl: this.redirectReplaceUrl });
    }
  }

  onMultiAccountLoginResult(success: boolean) {
    if (success) {
      this.onLoginSuccess();
    } else {
      this.cancelLogin();
    }
  }

  on2FAVerifySuccess(loginUserTokens: LoginUserToken[]) {
    this.twoFactorToken = null;
    if (loginUserTokens.length === 1 && this.authenticationService.loginByToken(loginUserTokens[0].token)) {
      this.onLoginSuccess();
    } else if (loginUserTokens.length > 1) {
      this.loginUserTokens = loginUserTokens;
      this.cd.markForCheck();
    } else {
      this.unexpectedError = true;
      this.cd.markForCheck();
    }
  }

  on2FACancel() {
    this.twoFactorToken = null;
    this.cd.markForCheck();
  }

  on2FASetupComplete() {
    const authToken = this.twoFactorSetupContext?.authToken;
    this.twoFactorSetupContext = null;
    if (authToken && this.authenticationService.loginByToken(authToken)) {
      this.onLoginSuccess();
    } else {
      this.unexpectedError = true;
      this.cd.markForCheck();
    }
  }

  openPrivacyPdf(): void {
    this.privacyService.openPrivacyPdf();
  }

  cancelLogin() {
    this.loginResult.emit(false);
    this.resetErrors();
    this.loginUserTokens = [];
    this.twoFactorToken = null;
    this.twoFactorSetupContext = null;
  }

  private resetErrors(): void {
    this.unexpectedError = false;
    this.unexpectedServerError = false;
    this.errorBadCredentials = false;
    this.errorOffline = false;
  }
}
